/**
 * Row shaping and sensor fitting for the live CALIBRATION page.
 *
 * Pure functions only — no Solid, no DOM, no fetching — so the whole numerical
 * path is unit tested without a browser. The page is a thin reactive wrapper
 * over these.
 *
 * The measurement chain: live merged rows (kph, one row per sample) → filtered
 * CalibrationSamples (grade, window, tack, sector) → a per-tack apparent-wind
 * mean per sensor → the offset/mast fit in windTriangleMath → corrected rows
 * for the violins.
 */

import { passesFoilingFilter, type FoilingFilter } from "./foilingStateFilter";
import {
  angleSubtract,
  awaCorrected,
  computeTrueWind,
  recommendOffsetsFromApparent,
  tackLeeway,
  wrap180,
  type ApparentRecommendation,
  type Tack,
  type TackApparent,
} from "./windTriangleMath";

// ---------------------------------------------------------------------------
// Channels and gates
// ---------------------------------------------------------------------------

/**
 * The only channels this page reads, sent as `?channels=` so both read tiers
 * project down to them.
 *
 * `TACK` is deliberately absent. It is a UTF8 column in the live parquet, and
 * the deep read types every requested channel as float — DuckDB would average a
 * string. Tack comes from the sign of Twa_deg, which is what the violins use
 * anyway.
 *
 * Canonical `Awa_deg`/`Aws_kph` are absent too: they alias the team-model
 * solution, not either physical vane, so they cannot be calibrated per sensor.
 */
export const LIVE_CALIBRATION_CHANNELS: readonly string[] = [
  "Bsp_kph",
  "Hdg_deg",
  "Awa_bow_deg",
  "Awa_mhu_deg",
  "Aws_bow_kph",
  "Aws_mhu_kph",
  "Twa_deg",
  "Tws_kph",
  "Twa_tgt_deg",
  "Lwy_deg",
  "WING_rot_deg",
  "GRADE",
  // Ride-height split: the processor already derives Foiling_state (0/1/2) with the pipeline's
  // BSP/heel rule, and Heel_n_deg is the fallback the derivation needs when it did not.
  "Foiling_state",
  "Heel_n_deg",
];

/**
 * Live grading is fail-open: the processor starts every sample at 3 and only
 * demotes to 1 (post-maneuver, turning, off the foils, below target). So
 * `GRADE > 1` means "a steady-state sample worth calibrating on".
 */
export const LIVE_CALIBRATION_MIN_GRADE_EXCLUSIVE = 1;

/** Below this the boat is not sailing usefully; same gate as the day page. */
export const MIN_BSP_KPH = 5;

/** Fewer samples than this on either tack and the fit is not reported. */
export const MIN_TACK_SAMPLES = 20;

/**
 * A real sensor offset lives inside about ±2°.
 *
 * Larger is not refused — the fit is what the data says — but it is a statement
 * about the DATA, not about the sensor: mis-graded rows, a maneuver inside the
 * window, or a sector gate catching a transition will all open a tack split
 * that looks like a bias. The page flags it so the offset is not applied to the
 * boat before the window is checked. (BIAS_LIMIT_DEG, ±30°, is the far wider
 * bracket the solver refuses to search beyond.)
 */
export const PLAUSIBLE_OFFSET_DEG = 2;

/** Whether a fitted offset is inside the physically expected band. */
export function offsetPlausibility(offsetDeg: number): "ok" | "suspect" {
  return Math.abs(offsetDeg) <= PLAUSIBLE_OFFSET_DEG ? "ok" : "suspect";
}

/** Sector gates on |TWA|, matching the day/dataset Calibration pages. */
export const CALIBRATION_SECTOR_ABS_TWA = {
  upwind: [30, 75],
  downwind: [120, 155],
} as const;

/** Default window when nothing is brushed: the last hour of the live day. */
export const LIVE_CALIBRATION_DEFAULT_WINDOW_MS = 60 * 60 * 1000;

/** Past this the read is refused — one boat, but still a parquet sweep. */
export const LIVE_CALIBRATION_MAX_WINDOW_MS = 180 * 60 * 1000;

/** Past this the window is served but flagged as slow. */
export const LIVE_CALIBRATION_WARN_WINDOW_MS = 60 * 60 * 1000;

export type CalibrationSector = "upwind" | "downwind";
export type SensorName = "bow" | "mhu";

/** Per-sensor channel names. */
export const SENSOR_CHANNELS: Record<SensorName, { awa: string; aws: string; label: string }> = {
  bow: { awa: "Awa_bow_deg", aws: "Aws_bow_kph", label: "BOW" },
  mhu: { awa: "Awa_mhu_deg", aws: "Aws_mhu_kph", label: "MHU" },
};

// ---------------------------------------------------------------------------
// Rows → samples
// ---------------------------------------------------------------------------

export interface CalibrationSample {
  t: number;
  tack: Tack;
  bsp: number;
  hdg: number;
  /** Signed instrument TWA, wrapped to ±180. Drives tack and the sector gate. */
  twa: number;
  tws: number | null;
  awaBow: number | null;
  awaMhu: number | null;
  awsBow: number | null;
  awsMhu: number | null;
  twaTgt: number | null;
  lwy: number | null;
  wingRot: number | null;
}

/** Why rows did not make it through, so an empty page can say which gate emptied it. */
export interface SampleRejections {
  total: number;
  noGrade: number;
  lowGrade: number;
  outsideWindow: number;
  noTack: number;
  outsideSector: number;
  lowBsp: number;
  /** Rows whose ride-height state is not the selected one, or could not be read at all. */
  wrongFoilingState: number;
  incomplete: number;
}

export interface SampleResult {
  samples: CalibrationSample[];
  rejections: SampleRejections;
}

type Row = Record<string, unknown>;

function num(row: Row, key: string): number | null {
  const v = row[key];
  if (v === undefined || v === null || v === "") {return null;}
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** GRADE as written by the stream processor; casing varies by tier and consumer. */
export function rowGrade(row: Row): number | null {
  const raw = row.GRADE ?? row.Grade ?? row.grade;
  if (raw === undefined || raw === null || raw === "") {return null;}
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Tack from the sign of the instrument TWA: negative is port, positive is
 * starboard. Exactly 0 (and a missing TWA) is no tack, not starboard — head to
 * wind is not a calibration sample.
 */
export function rowTack(row: Row): Tack | null {
  const twa = num(row, "Twa_deg") ?? num(row, "twa_deg");
  if (twa === null) {return null;}
  const w = wrap180(twa);
  if (w === 0) {return null;}
  return w < 0 ? "port" : "stbd";
}

function rowTimestamp(row: Row): number | null {
  const ts = num(row, "timestamp");
  if (ts !== null) {return ts;}
  const dt = row.Datetime ?? row.datetime;
  if (dt instanceof Date) {return dt.getTime();}
  if (typeof dt === "string") {
    const parsed = new Date(dt).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function inSector(absTwa: number, sector: CalibrationSector): boolean {
  const [lo, hi] = CALIBRATION_SECTOR_ABS_TWA[sector];
  return absTwa > lo && absTwa < hi;
}

/**
 * Filter and shape merged live rows into calibration samples.
 *
 * Order matters only for the rejection tally — every gate is independent — but
 * it is arranged cheapest-first so a long window is not paying for angle maths
 * on rows a grade check already excluded.
 */
export function toCalibrationSamples(
  rows: Row[],
  opts: { startMs: number; endMs: number; sector: CalibrationSector; foiling?: FoilingFilter }
): SampleResult {
  const foiling: FoilingFilter = opts.foiling ?? "ALL";
  const rejections: SampleRejections = {
    total: rows.length,
    noGrade: 0,
    lowGrade: 0,
    outsideWindow: 0,
    noTack: 0,
    outsideSector: 0,
    lowBsp: 0,
    wrongFoilingState: 0,
    incomplete: 0,
  };
  const samples: CalibrationSample[] = [];

  for (const row of rows) {
    const grade = rowGrade(row);
    if (grade === null) {
      rejections.noGrade++;
      continue;
    }
    if (grade <= LIVE_CALIBRATION_MIN_GRADE_EXCLUSIVE) {
      rejections.lowGrade++;
      continue;
    }

    const t = rowTimestamp(row);
    if (t === null || t < opts.startMs || t > opts.endMs) {
      rejections.outsideWindow++;
      continue;
    }

    const tack = rowTack(row);
    if (tack === null) {
      rejections.noTack++;
      continue;
    }

    const twa = wrap180(num(row, "Twa_deg") as number);
    if (!inSector(Math.abs(twa), opts.sector)) {
      rejections.outsideSector++;
      continue;
    }

    const bsp = num(row, "Bsp_kph");
    if (bsp === null || bsp <= MIN_BSP_KPH) {
      rejections.lowBsp++;
      continue;
    }

    if (!passesFoilingFilter(row, foiling)) {
      rejections.wrongFoilingState++;
      continue;
    }

    const hdg = num(row, "Hdg_deg");
    if (hdg === null) {
      rejections.incomplete++;
      continue;
    }

    samples.push({
      t,
      tack,
      bsp,
      hdg,
      twa,
      tws: num(row, "Tws_kph"),
      awaBow: num(row, "Awa_bow_deg"),
      awaMhu: num(row, "Awa_mhu_deg"),
      awsBow: num(row, "Aws_bow_kph"),
      awsMhu: num(row, "Aws_mhu_kph"),
      twaTgt: num(row, "Twa_tgt_deg"),
      lwy: num(row, "Lwy_deg"),
      wingRot: num(row, "WING_rot_deg"),
    });
  }

  return { samples, rejections };
}

export function sampleAwa(s: CalibrationSample, sensor: SensorName): number | null {
  return sensor === "bow" ? s.awaBow : s.awaMhu;
}

export function sampleAws(s: CalibrationSample, sensor: SensorName): number | null {
  return sensor === "bow" ? s.awsBow : s.awsMhu;
}

// ---------------------------------------------------------------------------
// Per-tack aggregation
// ---------------------------------------------------------------------------

export interface TackAggregate {
  port: TackApparent | null;
  stbd: TackApparent | null;
  nPort: number;
  nStbd: number;
  /** Mean heading per tack, needed to turn each tack's TWA into a TWD. */
  hdgPort: number | null;
  hdgStbd: number | null;
  /** Leeway magnitude; signed per tack at solve time by tackLeeway. */
  lwy: number;
  /** Mean |Twa_tgt_deg| over the window, or null when the target is absent. */
  targetAbsTwa: number | null;
  meanWingRotPort: number | null;
  meanWingRotStbd: number | null;
  /** How many speed/wind bins held both tacks. Zero means nothing was comparable. */
  matchedBins: number;
  /** Samples per tack BEFORE matching, to tell "sailed one gybe" from "never overlapped". */
  rawPort: number;
  rawStbd: number;
}

/**
 * Bin widths for pairing the tacks like for like, in kph.
 *
 * Boat speed is binned tightly because it is the confound being removed and any
 * width left inside a bin leaks straight back into the offset: at 2 kph the
 * residual was still ~0.2 deg, which is a tenth of the whole plausible range.
 * Live data arrives at ~10 Hz, so narrow bins are affordable.
 */
export const MATCH_BSP_BIN_KPH = 1;
export const MATCH_TWS_BIN_KPH = 2;

function mean(values: number[]): number {
  let sum = 0;
  for (const v of values) {sum += v;}
  return sum / values.length;
}

function weightedMean(values: number[], weights: number[]): number {
  let sum = 0;
  let wsum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i] * weights[i];
    wsum += weights[i];
  }
  return wsum > 0 ? sum / wsum : 0;
}

/** Headings wrap, so average them as unit vectors. */
function circularMean(degrees: number[], weights: number[]): number {
  let x = 0;
  let y = 0;
  for (let i = 0; i < degrees.length; i++) {
    const r = (degrees[i] * Math.PI) / 180;
    x += Math.cos(r) * weights[i];
    y += Math.sin(r) * weights[i];
  }
  if (x === 0 && y === 0) {return 0;}
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return deg < 0 ? deg + 360 : deg;
}

interface BinStats {
  awa: number[];
  aws: number[];
  bsp: number[];
  hdg: number[];
  wingRot: number[];
}

function emptyBin(): BinStats {
  return { awa: [], aws: [], bsp: [], hdg: [], wingRot: [] };
}

/**
 * Collapse each tack to the single apparent-wind observation the fit needs, comparing
 * the two tacks LIKE FOR LIKE.
 *
 * Samples are binned by boat speed and wind speed, and only bins holding both tacks
 * count. This is the whole difference between a calibration and a performance
 * observation: |AWA| depends on how fast the boat is going, so a boat that sails one
 * gybe 8 kph quicker than the other shows an angle split that is not a sensor bias at
 * all. Downwind that confound is severe — the tacks were genuinely 53.6 against 46.0
 * kph — and it is what made an earlier version recommend an 18° offset.
 *
 * Each matched bin is weighted by its thinner tack, so a bin with 200 port samples and
 * 3 starboard ones cannot dominate the answer.
 *
 * The AWA mean is of the SIGNED angle (port negative), which is what carries the
 * anti-symmetric bias; taking magnitudes first would erase the very split being fitted.
 */
export function aggregateTacks(samples: CalibrationSample[], sensor: SensorName): TackAggregate {
  const bins = new Map<string, Record<Tack, BinStats>>();
  const lwy: number[] = [];
  const target: number[] = [];

  for (const s of samples) {
    const awa = sampleAwa(s, sensor);
    const aws = sampleAws(s, sensor);
    if (awa === null || aws === null) {continue;}

    const bspBin = Math.round(s.bsp / MATCH_BSP_BIN_KPH);
    // TWS is used for matching when present; without it, boat speed alone still
    // removes most of the confound.
    const twsBin = s.tws === null ? "x" : String(Math.round(s.tws / MATCH_TWS_BIN_KPH));
    const key = `${bspBin}|${twsBin}`;

    let bin = bins.get(key);
    if (!bin) {
      bin = { port: emptyBin(), stbd: emptyBin() };
      bins.set(key, bin);
    }
    const bucket = bin[s.tack];
    bucket.awa.push(wrap180(awa));
    bucket.aws.push(aws);
    bucket.bsp.push(s.bsp);
    bucket.hdg.push(s.hdg);
    if (s.wingRot !== null) {bucket.wingRot.push(s.wingRot);}

    if (s.lwy !== null) {lwy.push(Math.abs(s.lwy));}
    if (s.twaTgt !== null) {target.push(Math.abs(wrap180(s.twaTgt)));}
  }

  // Per-tack bin means, kept only where BOTH tacks are represented.
  const perTack: Record<Tack, { awa: number[]; aws: number[]; bsp: number[]; hdg: number[]; wingRot: number[] }> = {
    port: { awa: [], aws: [], bsp: [], hdg: [], wingRot: [] },
    stbd: { awa: [], aws: [], bsp: [], hdg: [], wingRot: [] },
  };
  const weights: number[] = [];
  let nPort = 0;
  let nStbd = 0;
  let matchedBins = 0;
  let rawPort = 0;
  let rawStbd = 0;

  for (const bin of bins.values()) {
    rawPort += bin.port.awa.length;
    rawStbd += bin.stbd.awa.length;
  }

  for (const bin of bins.values()) {
    if (bin.port.awa.length === 0 || bin.stbd.awa.length === 0) {continue;}
    matchedBins++;
    nPort += bin.port.awa.length;
    nStbd += bin.stbd.awa.length;
    weights.push(Math.min(bin.port.awa.length, bin.stbd.awa.length));
    for (const tack of ["port", "stbd"] as Tack[]) {
      const b = bin[tack];
      perTack[tack].awa.push(mean(b.awa));
      perTack[tack].aws.push(mean(b.aws));
      perTack[tack].bsp.push(mean(b.bsp));
      perTack[tack].hdg.push(circularMean(b.hdg, b.hdg.map(() => 1)));
      perTack[tack].wingRot.push(b.wingRot.length > 0 ? mean(b.wingRot) : NaN);
    }
  }

  const side = (tack: Tack): TackApparent | null => {
    const b = perTack[tack];
    if (b.awa.length === 0) {return null;}
    const awa = weightedMean(b.awa, weights);
    const aws = weightedMean(b.aws, weights);
    const bsp = weightedMean(b.bsp, weights);
    // vax..vwy are unused by the fit (it re-derives them from awa/aws/bsp), but
    // TackApparent carries them, so fill them from the mean observation.
    const awaRad = (awa * Math.PI) / 180;
    return {
      awa,
      aws,
      bsp,
      vax: aws * Math.cos(awaRad),
      vay: aws * Math.sin(awaRad),
      vbx: bsp,
      vby: 0,
      vwx: aws * Math.cos(awaRad) - bsp,
      vwy: aws * Math.sin(awaRad),
    };
  };

  const hdgOf = (tack: Tack): number | null => {
    const b = perTack[tack];
    return b.hdg.length > 0 ? circularMean(b.hdg, weights) : null;
  };

  const wingRotOf = (tack: Tack): number | null => {
    const vals: number[] = [];
    const w: number[] = [];
    perTack[tack].wingRot.forEach((v, i) => {
      if (Number.isFinite(v)) {
        vals.push(v);
        w.push(weights[i]);
      }
    });
    return vals.length > 0 ? weightedMean(vals, w) : null;
  };

  return {
    port: side("port"),
    stbd: side("stbd"),
    nPort,
    nStbd,
    hdgPort: hdgOf("port"),
    hdgStbd: hdgOf("stbd"),
    lwy: lwy.length > 0 ? mean(lwy) : 0,
    targetAbsTwa: target.length > 0 ? mean(target) : null,
    meanWingRotPort: wingRotOf("port"),
    meanWingRotStbd: wingRotOf("stbd"),
    matchedBins,
    rawPort,
    rawStbd,
  };
}

// ---------------------------------------------------------------------------
// The fit
// ---------------------------------------------------------------------------

/** The wind each tack reports, before and after a correction is applied. */
export interface WindOutcome {
  twsPort: number;
  twsStbd: number;
  dTws: number;
  /** Null when the window carries no usable headings, so TWD cannot be formed. */
  twdPort: number | null;
  twdStbd: number | null;
  dTwd: number | null;
}

export interface SensorSolution {
  sensor: SensorName;
  available: boolean;
  /** Why the fit was not attempted, when available is false. */
  reason: string | null;
  nPort: number;
  nStbd: number;
  lwy: number;
  targetAbsTwa: number | null;
  meanWingRotPort: number | null;
  meanWingRotStbd: number | null;
  matchedBins: number;
  /** True wind the two tacks agree on, with no correction and with the recommendation. */
  windBefore: WindOutcome | null;
  windAfter: WindOutcome | null;
  fit: ApparentRecommendation | null;
}

/**
 * Fit one sensor. `mastOverride` (MHU only) replaces the fitted rotation and the
 * offset is re-solved for it, so the operator's value stays angle-matched.
 */
export function solveSensor(
  agg: TackAggregate,
  sensor: SensorName,
  mastOverride?: number | null
): SensorSolution {
  const base = {
    sensor,
    nPort: agg.nPort,
    nStbd: agg.nStbd,
    lwy: agg.lwy,
    targetAbsTwa: agg.targetAbsTwa,
    meanWingRotPort: agg.meanWingRotPort,
    meanWingRotStbd: agg.meanWingRotStbd,
    matchedBins: agg.matchedBins,
    windBefore: null,
    windAfter: null,
  };

  if (agg.port === null || agg.stbd === null) {
    return {
      ...base,
      available: false,
      reason:
        agg.rawPort === 0 || agg.rawStbd === 0
          ? "needs samples on both tacks"
          : "the tacks never sailed at a comparable speed, so there is nothing to compare",
      fit: null,
    };
  }
  if (agg.nPort < MIN_TACK_SAMPLES || agg.nStbd < MIN_TACK_SAMPLES) {
    return {
      ...base,
      available: false,
      reason: `needs ${MIN_TACK_SAMPLES}+ speed-matched samples per tack (have ${agg.nPort} / ${agg.nStbd})`,
      fit: null,
    };
  }

  const fit = recommendOffsetsFromApparent({
    port: agg.port,
    stbd: agg.stbd,
    lwy: agg.lwy,
    correction: sensor,
    targetAbsTwa: agg.targetAbsTwa,
    mastOverride: sensor === "mhu" ? mastOverride ?? null : null,
  });

  return {
    ...base,
    available: true,
    reason: null,
    windBefore: windOutcome(agg, 0, 0),
    windAfter: windOutcome(agg, fit.off, fit.mast),
    fit,
  };
}

/**
 * The true wind each tack reports under a given correction.
 *
 * TWD needs the heading, which the fit itself never uses (Δ|TWA| is heading-free),
 * so it is formed here from each tack's mean heading. Two tacks that disagree on
 * the wind direction cannot both be right, which makes ΔTWD the plainest statement
 * of whether a correction actually helped.
 */
export function windOutcome(agg: TackAggregate, off: number, mast: number): WindOutcome | null {
  if (agg.port === null || agg.stbd === null) {return null;}

  const side = (aw: TackApparent, tack: Tack, hdg: number | null) => {
    const awa = awaCorrected(wrap180(aw.awa), tack, off, mast);
    // Heading only shifts TWD, never TWS or TWA, so a missing one still gives speed.
    const tw = computeTrueWind(aw.aws, awa, aw.bsp, hdg ?? 0, tackLeeway(agg.lwy, tack));
    return { tws: tw.tws, twd: hdg === null ? null : tw.twd };
  };

  const port = side(agg.port, "port", agg.hdgPort);
  const stbd = side(agg.stbd, "stbd", agg.hdgStbd);

  return {
    twsPort: port.tws,
    twsStbd: stbd.tws,
    dTws: port.tws - stbd.tws,
    twdPort: port.twd,
    twdStbd: stbd.twd,
    dTwd:
      port.twd === null || stbd.twd === null ? null : angleSubtract(port.twd, stbd.twd),
  };
}

// ---------------------------------------------------------------------------
// Per-row correction
// ---------------------------------------------------------------------------

export interface CorrectedSample {
  t: number;
  tack: Tack;
  /** Signed instrument TWA, kept so the violin can split the row by tack. */
  twaSigned: number;
  awaCorr: number;
  twaCorr: number;
  twsCorr: number;
}

/**
 * Apply a fitted bias to every sample and re-solve the triangle.
 *
 * The offsets are fitted on per-tack means, but applying them row by row is what
 * shows whether the correction holds across the whole window or only on average
 * — which is the entire point of plotting the corrected distribution.
 */
export function applyCorrections(
  samples: CalibrationSample[],
  sensor: SensorName,
  off: number,
  mast: number
): CorrectedSample[] {
  const out: CorrectedSample[] = [];
  for (const s of samples) {
    const awa = sampleAwa(s, sensor);
    const aws = sampleAws(s, sensor);
    if (awa === null || aws === null) {continue;}
    const awaCorr = awaCorrected(wrap180(awa), s.tack, off, mast);
    const lee = tackLeeway(s.lwy ?? 0, s.tack);
    const tw = computeTrueWind(aws, awaCorr, s.bsp, s.hdg, lee);
    out.push({
      t: s.t,
      tack: s.tack,
      twaSigned: s.twa,
      awaCorr,
      twaCorr: tw.twa,
      twsCorr: tw.tws,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Violin shaping
// ---------------------------------------------------------------------------

/**
 * One violin row. `Twa_signed` is the raw instrument TWA, which is what Violin
 * splits port/starboard on; `SENSOR` is the x band (BOW / MHU / BSP).
 */
export interface ViolinRow {
  SENSOR: string;
  Value: number;
  Twa_signed: number;
}

export interface ViolinGroup {
  name: string;
  color: string;
}

export interface ViolinChart {
  series: Array<{
    xaxis: { name: string };
    yaxis: { name: string; dataField: string };
    groupField: string;
    groups: ViolinGroup[];
  }>;
}

export function buildViolinChart(yLabel: string, groups: ViolinGroup[]): ViolinChart {
  return {
    series: [
      {
        xaxis: { name: "Sensor" },
        yaxis: { name: yLabel, dataField: "Value" },
        groupField: "SENSOR",
        groups,
      },
    ],
  };
}

/** Rows for one band of a violin, dropping samples where the value is missing. */
export function buildViolinRows<T>(
  items: T[],
  band: string,
  pick: (item: T) => number | null,
  twaSigned: (item: T) => number
): ViolinRow[] {
  const out: ViolinRow[] = [];
  for (const item of items) {
    const value = pick(item);
    if (value === null || !Number.isFinite(value)) {continue;}
    out.push({ SENSOR: band, Value: value, Twa_signed: twaSigned(item) });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The time window
// ---------------------------------------------------------------------------

export interface TimeWindow {
  start: Date;
  end: Date;
}

export function defaultLiveWindow(nowMs: number = Date.now()): TimeWindow {
  return { start: new Date(nowMs - LIVE_CALIBRATION_DEFAULT_WINDOW_MS), end: new Date(nowMs) };
}

export type WindowState = "ok" | "warn" | "tooLong";

export function windowState(window: TimeWindow): WindowState {
  const span = window.end.getTime() - window.start.getTime();
  if (span > LIVE_CALIBRATION_MAX_WINDOW_MS) {return "tooLong";}
  if (span > LIVE_CALIBRATION_WARN_WINDOW_MS) {return "warn";}
  return "ok";
}

export function windowMinutes(window: TimeWindow): number {
  return Math.max(0, Math.round((window.end.getTime() - window.start.getTime()) / 60000));
}
